import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import { createTheme, ThemeProvider } from '@mui/material';
import {
  DayView,
  WeekView,
  Appointments,
  ViewSwitcher,
  DateNavigator,
  TodayButton,
  Scheduler as MuiSchedular
} from '@devexpress/dx-react-scheduler-material-ui';
import { Scheduler, AppointmentModel, ViewState } from '@devexpress/dx-react-scheduler';
import CustomToolbar from './CustomToolbar';
import CustomAppointmentTooltip from './CustomAppointmentTooltip';
import { calendarActions } from '../../../../store/calendar';
import AppointmentC from './AppointmentC';
import AppointmentComponent from './AppointmentComponent';
import { RootState } from '../../../../store';
import { loaderActions } from '../../../../store/loader';
import { Appointment } from '../../../../../domain/entities';
import { useAppointmentService } from '../../../../hooks';

const theme = createTheme({
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          height: '65px !important',
          '& > div.Label-label': {
            height: '65px !important',
            lineHeight: '65px !important'
          },
          '& > div.Label-emptyLabel': {
            height: '32px !important'
          }
        }
      }
    }
  }
});

interface CustomAppointmentModel extends AppointmentModel {
  color: string;
}

const [startHour, endHour] = [8, 21];

const Calendar = () => {
  const AppointmentService = useAppointmentService();
  const dispatch = useDispatch();
  const calendar = useSelector((state: RootState) => state.calendar);

  const isLoading = (state: boolean) => {
    dispatch(loaderActions.setLoading(state));
  };

  const [appointments, setAppointments] = React.useState<CustomAppointmentModel[]>([]);

  React.useEffect(() => {
    isLoading(true);
    AppointmentService.listAll()
      .then((data) => {
        const mappedData = mapToCustomModels(data);
        setAppointments(mappedData);
      })
      .catch((err) => console.log(err))
      .finally(() => isLoading(false));
  }, []);

  const mapToCustomModels = (appointments: Appointment[]) => {
    return appointments.map((appointment) => {
      return {
        ...appointment,
        title: `${appointment.specialty.name} - ${appointment.doctor.name}`,
        color: appointment.specialty.color
      };
    });
  };

  const setCurrentDate = (currentDate: Date) =>
    dispatch(calendarActions.setCurrentDate(currentDate.toISOString()));

  const setCurrentViewName = (currentViewName: string) =>
    dispatch(calendarActions.setCurrentViewName(currentViewName));

  const rootComponent = (props: Scheduler.RootProps) => <MuiSchedular.Root {...props} />;

  return (
    <ThemeProvider theme={theme}>
      <div></div>
      <Paper>
        <Scheduler
          data={appointments}
          rootComponent={rootComponent}
          height='auto'
          firstDayOfWeek={1}
          locale='es-EC'
        >
          <ViewState
            currentDate={calendar.currentDate}
            currentViewName={calendar.currentViewName}
            onCurrentViewNameChange={setCurrentViewName}
            onCurrentDateChange={setCurrentDate}
          />
          <WeekView displayName='Semanal' startDayHour={startHour} endDayHour={endHour} />
          <DayView displayName='Diario' startDayHour={startHour} endDayHour={endHour} />
          <Appointments
            appointmentComponent={AppointmentC}
            appointmentContentComponent={AppointmentComponent}
          />
          <CustomToolbar />
          <DateNavigator />
          <TodayButton messages={{ today: 'Hoy' }} />
          <ViewSwitcher />
          <CustomAppointmentTooltip />
        </Scheduler>
      </Paper>
    </ThemeProvider>
  );
};

export default Calendar;
